import { useEffect, useRef } from 'react';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Link2, Heading1, Heading2, Undo2, Redo2, Quote, Minus } from 'lucide-react';

const ToolbarBtn = ({ onClick, icon: Icon, title, tid, testId }) => (
  <button type="button" onClick={onClick} title={title} data-testid={`${testId}-${tid}-button`}
    className="w-8 h-8 rounded-md grid place-items-center jw-btn-secondary hover:opacity-90">
    <Icon className="w-4 h-4" />
  </button>
);

/**
 * Lightweight contentEditable WYSIWYG that stores HTML.
 * Toolbar buttons use document.execCommand — still universally supported
 * despite being marked deprecated. Good enough for content pages.
 */
const RichEditor = ({ value, onChange, placeholder, testId = 'rich-editor' }) => {
  const ref = useRef(null);

  useEffect(() => {
    if (!ref.current) return;
    // Sync external value into the contentEditable DOM whenever it changes
    // (e.g. loading a different page). Skip when identical to avoid stealing
    // the caret while the user is typing (typing triggers value === innerHTML
    // via the onInput callback).
    const next = value || '';
    if (ref.current.innerHTML !== next) {
      ref.current.innerHTML = next;
    }
  }, [value]);

  const exec = (command, arg) => {
    document.execCommand(command, false, arg);
    ref.current?.focus();
    onChange(ref.current?.innerHTML || '');
  };

  const insertLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) exec('createLink', url);
  };

  const toggleBlock = (tag) => exec('formatBlock', tag);

  return (
    <div className="jw-panel rounded-lg overflow-hidden" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-1 p-2 border-b" style={{ borderColor: 'var(--jw-border)', background: 'var(--jw-panel)' }}>
        <ToolbarBtn testId={testId} onClick={() => exec('bold')} icon={Bold} title="Bold" tid="bold" />
        <ToolbarBtn testId={testId} onClick={() => exec('italic')} icon={Italic} title="Italic" tid="italic" />
        <ToolbarBtn testId={testId} onClick={() => exec('underline')} icon={UnderlineIcon} title="Underline" tid="underline" />
        <span className="w-px h-5 mx-1" style={{ background: 'var(--jw-border)' }} />
        <ToolbarBtn testId={testId} onClick={() => toggleBlock('H1')} icon={Heading1} title="Heading 1" tid="h1" />
        <ToolbarBtn testId={testId} onClick={() => toggleBlock('H2')} icon={Heading2} title="Heading 2" tid="h2" />
        <ToolbarBtn testId={testId} onClick={() => toggleBlock('BLOCKQUOTE')} icon={Quote} title="Quote" tid="quote" />
        <span className="w-px h-5 mx-1" style={{ background: 'var(--jw-border)' }} />
        <ToolbarBtn testId={testId} onClick={() => exec('insertUnorderedList')} icon={List} title="Bullet list" tid="ul" />
        <ToolbarBtn testId={testId} onClick={() => exec('insertOrderedList')} icon={ListOrdered} title="Numbered list" tid="ol" />
        <ToolbarBtn testId={testId} onClick={insertLink} icon={Link2} title="Insert link" tid="link" />
        <ToolbarBtn testId={testId} onClick={() => exec('insertHorizontalRule')} icon={Minus} title="Divider" tid="hr" />
        <span className="w-px h-5 mx-1" style={{ background: 'var(--jw-border)' }} />
        <ToolbarBtn testId={testId} onClick={() => exec('undo')} icon={Undo2} title="Undo" tid="undo" />
        <ToolbarBtn testId={testId} onClick={() => exec('redo')} icon={Redo2} title="Redo" tid="redo" />
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerHTML)}
        data-testid={`${testId}-content`}
        data-placeholder={placeholder || 'Start writing...'}
        className="jw-prose min-h-64 max-h-[500px] overflow-auto p-4 outline-none jw-scrollbar"
        style={{ background: 'var(--jw-bg-alt)' }}
      />
    </div>
  );
};

export default RichEditor;
